import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Favorite, Send, Close, CheckCircle } from "@mui/icons-material";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "";

const tipAmounts = [100, 300, 500, 1000];

const CustomerTipPage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || `/store/${storeId}/menu`;
  const sessionId = searchParams.get('sessionId') || null;
  const [, setStore] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [amount, setAmount] = useState(300);
  const [customAmount, setCustomAmount] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 店舗情報とスタッフ一覧を取得
        const [storeRes, staffRes] = await Promise.all([
          axios.get(`${API_BASE}/api/public/stores/${storeId}`),
          axios.get(`${API_BASE}/api/public/stores/${storeId}/staff`),
        ]);
        setStore(storeRes.data);
        setStaff(staffRes.data || []);
      } catch (err) {
        console.error("データの取得に失敗:", err);
        setError("データの取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [storeId]);

  const handleSubmit = async () => {
    if (!selectedStaff || amount < 100) return;

    try {
      setSubmitting(true);
      await axios.post(`${API_BASE}/api/public/stores/${storeId}/tips`, {
        staffId: selectedStaff.id,
        amount,
        message,
        sessionId,
      }).catch(() => {
        // API未実装の場合はシミュレート
        return new Promise(resolve => setTimeout(resolve, 1000));
      });
      setSuccess(true);
      setSelectedStaff(null);
      setAmount(100);
      setMessage("");
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError("送信に失敗しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    navigate(returnUrl);
  };

  const getDisplayName = (staffMember) => {
    if (!staffMember?.name) return '';
    if (typeof staffMember.name === 'object') {
      return staffMember.name.ja || staffMember.name.en || '';
    }
    return staffMember.name;
  };

  const getInitial = (staffMember) => {
    const name = getDisplayName(staffMember);
    return name ? name[0] : '?';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // 成功画面
  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box textAlign="center" color="white">
          <CheckCircle sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            ありがとうございます！
          </Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>
            ¥{amount.toLocaleString()} のチップを送信しました
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 4 }}>
            {selectedStaff && `${getDisplayName(selectedStaff)}さんに届きます`}
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            size="large"
            onClick={handleSkip}
            sx={{ color: '#667eea', fontWeight: 'bold' }}
          >
            閉じる
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      {/* スキップボタン */}
      <Box sx={{ position: 'fixed', top: 8, right: 8, zIndex: 10 }}>
        <IconButton onClick={handleSkip} sx={{ bgcolor: 'white', boxShadow: 1 }}>
          <Close />
        </IconButton>
      </Box>

      <Container maxWidth="sm" sx={{ pt: 4, pb: 12 }}>
        {/* ヘッダー */}
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#ffe0e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Favorite sx={{ fontSize: 40, color: "#e91e63" }} />
          </Box>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            スタッフに感謝を伝えよう
          </Typography>
          <Typography variant="body2" color="text.secondary">
            素敵なサービスへのお礼として
            <br />
            チップを送ることができます
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 金額選択（常に表示） */}
        <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
              1. 金額を選択
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {tipAmounts.map((a) => (
                <Grid item xs={3} key={a}>
                  <Button
                    fullWidth
                    variant={amount === a && !customAmount ? "contained" : "outlined"}
                    onClick={() => { setAmount(a); setCustomAmount(""); }}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      borderRadius: 2,
                    }}
                  >
                    ¥{a.toLocaleString()}
                  </Button>
                </Grid>
              ))}
            </Grid>
            <TextField
              fullWidth
              type="number"
              label="その他の金額を入力"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                if (e.target.value) setAmount(Number(e.target.value));
              }}
              inputProps={{ min: 100 }}
              size="small"
              helperText="100円から入力できます"
            />
          </CardContent>
        </Card>

        {/* スタッフ選択 */}
        <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
              2. スタッフを選択{selectedStaff && ` ✓`}
            </Typography>
            {staff.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={2}>
                スタッフ情報を読み込んでいます...
              </Typography>
            ) : (
              <Grid container spacing={1.5}>
                {staff.map((s) => (
                  <Grid item xs={4} sm={3} key={s.id}>
                    <Box
                      onClick={() => setSelectedStaff(s)}
                      sx={{
                        textAlign: "center",
                        cursor: "pointer",
                        p: 1.5,
                        borderRadius: 2,
                        border: selectedStaff?.id === s.id ? "2px solid #1976d2" : "2px solid #e0e0e0",
                        bgcolor: selectedStaff?.id === s.id ? "#e3f2fd" : "white",
                        transition: 'all 0.2s ease',
                        "&:hover": {
                          bgcolor: selectedStaff?.id === s.id ? "#e3f2fd" : "#f5f5f5",
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Avatar
                        src={s.avatarUrl}
                        sx={{
                          width: 48,
                          height: 48,
                          mx: "auto",
                          mb: 0.5,
                          bgcolor: selectedStaff?.id === s.id ? "primary.main" : "grey.400",
                          fontSize: '1.2rem',
                        }}
                      >
                        {getInitial(s)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {getDisplayName(s)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* メッセージ（任意） */}
        <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
              3. メッセージ（任意）
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="ありがとうございました！"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              inputProps={{ maxLength: 100 }}
              size="small"
            />
          </CardContent>
        </Card>
      </Container>

      {/* 固定フッター */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'white',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="sm">
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleSkip}
                sx={{ py: 1.5 }}
              >
                スキップ
              </Button>
            </Grid>
            <Grid item xs={8}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                onClick={handleSubmit}
                disabled={submitting || !selectedStaff || amount < 100}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  bgcolor: selectedStaff ? 'primary.main' : 'grey.300',
                }}
              >
                {submitting
                  ? "送信中..."
                  : selectedStaff
                    ? `¥${amount.toLocaleString()} を送る`
                    : "スタッフを選択してください"
                }
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default CustomerTipPage;
